import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { parseISO } from 'date-fns';

import { Link } from '@/components/Link/Link';
import { TransactionMethod, TransactionMethodIcon } from '@/components/TransactionMethod';
import config from '@/config';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconCalendar from '@/icons/Calendar.svg';
import IconDownload from '@/icons/Download.svg';
import IconInvoice from '@/icons/Invoice.svg';
import { useGetArticleName } from '@/models/article';
import { getAmountWithQuantity, useInvoiceLineName } from '@/models/invoiceLine';
import {
  TransactionFromGet,
  useTransaction,
  useTransactionDestinationName,
  useTransactionOriginName,
} from '@/models/transaction';
import { formatDate } from '@/utils/date';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

export const Transaction = () => {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();

  const { transactionId } = useParams();
  const { data: transaction } = useTransaction(transactionId);

  if (!transaction) {
    return null;
  }

  const close = () => {
    setIsOpen(false);

    // Wait for the Drawer CSS exit animation to finish before navigating away
    setTimeout(() => navigate(`/accounting?tab=transactions`, { replace: true }), 150);
  };

  return (
    <Drawer isOpen={isOpen} onOpenChange={close}>
      <TransactionDrawerContent transaction={transaction} />
    </Drawer>
  );
};

const TransactionDrawerContent = ({ transaction }: { transaction: TransactionFromGet }) => {
  const { isMobile } = useViewPort();

  const origin = useTransactionOriginName(transaction);
  const destination = useTransactionDestinationName(transaction);

  return (
    <>
      <DrawerHeader
        subtitle={
          <Paragraph size="50" weight="medium">
            {formatCurrency(transaction.amount, transaction.currency)}
          </Paragraph>
        }
      >
        <Title size="300" weight="medium" as="h4">
          <Stack row gap="0.5rem" alignItems="center">
            <Trans id="accounting.transaction.invoices.header.title">
              <InlineText weight="bold">{origin}</InlineText> to{' '}
              <InlineText weight="bold">{destination}</InlineText>
            </Trans>
          </Stack>
        </Title>
      </DrawerHeader>
      <DrawerBody>
        <Stack padding={isMobile ? '1.5rem 1rem' : '1.5rem 2rem'} gap="2rem">
          <Stack row justifyContent="space-between" gap="0.5rem">
            <PaymentMethod transaction={transaction} />
            <ProofOfExternalPayment transaction={transaction} />
          </Stack>
          {!!transaction.reference && <PaymentReference transaction={transaction} />}
          <OperationDate transaction={transaction} />
          {!!transaction.invoices.length && <Invoices transaction={transaction} />}
          <InvoiceLines transaction={transaction} />
        </Stack>
      </DrawerBody>
    </>
  );
};

const PaymentMethod = ({ transaction }: { transaction: TransactionFromGet }) => {
  return (
    <Stack gap="0.5rem">
      <Title as="h2">
        <Trans id="accounting.transaction.payment-method.title">Payment method</Trans>
      </Title>
      <Stack row gap="0.5rem" alignItems="center">
        <TransactionMethodIcon
          transaction={transaction}
          style={{ fontSize: '1.5rem', color: 'var(--color-text-secondary-black)' }}
        />
        <Paragraph>
          <TransactionMethod transaction={transaction} />
        </Paragraph>
      </Stack>
    </Stack>
  );
};

const ProofOfExternalPayment = ({ transaction }: { transaction: TransactionFromGet }) => {
  const proofOfExternalPayment =
    transaction.media.find((medium) => medium.type === 'proof-of-external-payment') ??
    transaction.invoices
      .flatMap((invoice) => invoice.media)
      .find((medium) => medium.type === 'proof-of-external-payment');
  const { t } = useLingui();

  if (!proofOfExternalPayment) {
    return null;
  }

  return (
    <Button
      variant="style-less"
      ariaLabel={t({
        id: 'accounting.transaction.proof-of-external-payment.download.aria-label',
        message: 'Download proof of external payment',
      })}
      href={proofOfExternalPayment.url}
      download
    >
      <Stack row alignItems="center" gap="0.2rem" flexWrap="nowrap">
        <IconInvoice style={{ fontSize: '1rem', color: 'var(--color-text-secondary-black)' }} />
        <Paragraph
          color="secondary"
          size="200"
          style={{ textDecoration: 'underline', textUnderlineOffset: '3px' }}
        >
          <Trans id="accounting.transaction.proof-of-external-payment.download">Receipt</Trans>
        </Paragraph>
      </Stack>
    </Button>
  );
};

const PaymentReference = ({ transaction }: { transaction: TransactionFromGet }) => {
  return (
    <Stack gap="0.5rem">
      <Title as="h2">
        <Trans id="accounting.transaction.payment-reference.title">Payment reference</Trans>
      </Title>
      <Paragraph>{transaction.reference}</Paragraph>
    </Stack>
  );
};

const OperationDate = ({ transaction }: { transaction: TransactionFromGet }) => {
  const operationDate = transaction.startedAt
    ? formatDate(new Date(transaction.startedAt), { dateStyle: 'medium' })
    : '-';

  return (
    <Stack gap="0.5rem">
      <Title as="h2">
        <Trans id="accounting.transaction.operation-date.title">Operation date</Trans>
      </Title>

      <Stack row gap="0.5rem" alignItems="center">
        <IconCalendar style={{ fontSize: '1.5rem' }} />
        <Paragraph>{operationDate}</Paragraph>
      </Stack>
    </Stack>
  );
};

const Invoices = ({ transaction }: { transaction: TransactionFromGet }) => {
  const { t } = useLingui();

  const columnWidths = {
    invoice: 'minmax(120px, 1fr)',
    servicesAmount: 'minmax(120px, 1fr)',
    action: '60px',
  };

  return (
    <Stack gap="0.5rem">
      <Title as="h2">
        <Trans id="accounting.transaction.invoices.title">Invoices</Trans>
      </Title>
      <Box padding="0" gap="1rem">
        <Table
          ariaLabel={t({
            id: 'accounting.transaction.invoices.table.label',
            message: 'Invoices table',
          })}
          columnWidths={[columnWidths.invoice, columnWidths.servicesAmount, columnWidths.action]}
        >
          <Header>
            <Row>
              <Column>
                <Trans id="accounting.transaction.invoices.table.column.invoice.label">
                  Invoice
                </Trans>
              </Column>

              <Column>
                <Trans id="accounting.transaction.invoices.table.column.services-amount.label">
                  Services amount
                </Trans>
              </Column>
              <Column></Column>
            </Row>
          </Header>

          <Body>
            {transaction.invoices.map((invoice) => (
              <InvoiceRow key={invoice.id} invoice={invoice} transaction={transaction} />
            ))}
          </Body>
        </Table>
      </Box>
    </Stack>
  );
};

const InvoiceRow = ({
  invoice,
  transaction,
}: {
  invoice: TransactionFromGet['invoices'][number];
  transaction: TransactionFromGet;
}) => {
  const totalServicesAmount = transaction.invoiceLines
    .filter((line) => line.invoiceId === invoice.id)
    .reduce((sum, line) => sum + getAmountWithQuantity(line.price), 0);

  return (
    <Row>
      <Column>
        <Stack alignItems="flex-start">
          <Button
            variant="link-subtle"
            to={`/accounting/invoices/${invoice.id}`}
            className="text-primary"
          >
            {invoice.number}
          </Button>
          <Tooltip
            content={
              invoice.paymentDueDate ? (
                <Trans id="accounting.transaction.invoices.table.column.payment-due.date">
                  Payment due date
                </Trans>
              ) : (
                <Trans id="accounting.transaction.invoices.table.column.payment-due.no-date">
                  No payment due date
                </Trans>
              )
            }
          >
            <Button variant="style-less" size="small">
              <InlineText size="200">
                {invoice.paymentDueDate
                  ? formatDate(parseISO(invoice.paymentDueDate), {
                      dateStyle: 'medium',
                    })
                  : '-'}
              </InlineText>
            </Button>
          </Tooltip>
        </Stack>
      </Column>
      <Column>
        <InlineText>{formatCurrency(totalServicesAmount, invoice.currency)}</InlineText>
      </Column>
      <Column>
        {!!invoice.media.length && (
          <Button
            iconOnly
            variant="secondary"
            tooltip={
              <Trans id="accounting.transaction.invoices.table.column.download-invoice.tooltip">
                Download invoice
              </Trans>
            }
            href={`${config.apiUrl}/invoices/${invoice.id}/media`}
            download
          >
            <IconDownload />
          </Button>
        )}
      </Column>
    </Row>
  );
};

const InvoiceLines = ({ transaction }: { transaction: TransactionFromGet }) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();

  const columnWidths = {
    service: 'minmax(180px, auto)',
    article: isMobile ? false : 'minmax(180px, 1fr)',
    request: isMobile ? false : 'minmax(100px, 1fr)',
    amount: 'minmax(100px, auto)',
  };

  return (
    <Stack gap="0.5rem">
      <Title as="h2">
        <Trans id="accounting.transaction.services.title">Services associated</Trans>
      </Title>
      <Box padding="0" gap="1rem">
        <Table
          ariaLabel={t({
            id: 'accounting.transaction.services.table.label',
            message: 'Associated services',
          })}
          columnWidths={[
            columnWidths.service,
            columnWidths.article,
            columnWidths.request,
            columnWidths.amount,
          ]}
        >
          <Header>
            <Row>
              <Column>
                <Trans id="accounting.transaction.services.table.column.service.label">
                  Service
                </Trans>
              </Column>
              {!isMobile && (
                <Column>
                  <Trans id="accounting.transaction.services.table.column.article.label">
                    Item
                  </Trans>
                </Column>
              )}
              {!isMobile && (
                <Column>
                  <Trans id="accounting.transaction.services.table.column.request.label">
                    Request
                  </Trans>
                </Column>
              )}
              <Column justifyContent="flex-end">
                <Trans id="accounting.transaction.services.table.column.amount.label">Amount</Trans>
              </Column>
            </Row>
          </Header>

          <Body>
            {transaction.invoiceLines.map((invoiceLine) => (
              <InvoiceLineRow key={invoiceLine.id} invoiceLine={invoiceLine} />
            ))}
          </Body>
        </Table>
      </Box>
    </Stack>
  );
};

const InvoiceLineRow = ({
  invoiceLine,
}: {
  invoiceLine: TransactionFromGet['invoiceLines'][number];
}) => {
  const { isMobile } = useViewPort();
  const name = useInvoiceLineName(invoiceLine);
  const getArticleName = useGetArticleName();
  const request = invoiceLine.request;

  return (
    <Row>
      <Cell>
        <Stack>
          {name}
          {isMobile && (
            <Stack row gap="0.25rem">
              {!!invoiceLine.article && (
                <>
                  <Link
                    to={`/requests/${request.id}/articles/${invoiceLine.article.id}`}
                    className="reset-link"
                  >
                    <InlineText size="200">
                      {getArticleName({ article: invoiceLine.article })}
                    </InlineText>
                  </Link>{' '}
                  •{' '}
                </>
              )}
              <Link to={`/requests/${request.id}`} className="reset-link">
                <InlineText size="200">{request.reference}</InlineText>
              </Link>
            </Stack>
          )}
        </Stack>
      </Cell>
      {!isMobile && (
        <Cell>
          {invoiceLine.article ? (
            <Button
              variant="link-subtle"
              to={`/requests/${request.id}/articles/${invoiceLine.article.id}`}
              className="text-primary"
              style={{ textAlign: 'left' }}
            >
              {getArticleName({ article: invoiceLine.article })}
            </Button>
          ) : (
            '-'
          )}
        </Cell>
      )}
      {!isMobile && (
        <Cell>
          <Button variant="link-subtle" to={`/requests/${request.id}`} className="text-primary">
            {request?.reference}
          </Button>
        </Cell>
      )}
      <Cell justifyContent="flex-end">
        {formatCurrency(getAmountWithQuantity(invoiceLine.price), invoiceLine.price.currency)}
      </Cell>
    </Row>
  );
};
